import {
  MitoIDO,
  MitoIDOSubscriber,
  MitoClaimReference,
  MitoIDOSubscription
} from '@injectivelabs/sdk-ts'
import { BigNumber, BigNumberInBase } from '@injectivelabs/utils'
import {
  sharedToBalanceInWei,
  sharedToBalanceInToken,
  sharedToBalanceInTokenInBase,
  sharedConvertTimestampToMilliseconds
} from '@shared/utils/formatter'
import { getDefaultTokenMeta } from '@/app/client/utils/mito'
import {
  testnetWhitelistedIDOs,
  mainnetBlacklistedIDOs,
  mainnetWhitelistedIDOs,
  hideClaimedTokenVaults,
  testnetBlacklistedMarketId,
  launchpadDescriptionMapping
} from '@/app/data/launchpad'
import {
  IS_TESTNET,
  IS_MAINNET,
  LAUNCHPAD_DEFAULT_MAX_CAP,
  TESTNET_WHITELIST_TIMESTAMP
} from '@/app/utils/constant'
import {
  UIIDO,
  UiMarket,
  IDOStatus,
  UIIDOSubscriber,
  UIClaimReference,
  UIIDOSubscription
} from '@/types'

export class UiLaunchpadTransformer {
  static idoToUIIdo(ido: MitoIDO): UIIDO | undefined {
    const appStore = useAppStore()
    const spotStore = useSpotStore()
    const sharedWalletStore = useSharedWalletStore()

    if (
      (IS_TESTNET &&
        !testnetWhitelistedIDOs.includes(ido.contractAddress) &&
        ido.status !== IDOStatus.Created &&
        ido.startTime < TESTNET_WHITELIST_TIMESTAMP) ||
      (IS_TESTNET && testnetBlacklistedMarketId.includes(ido.marketId))
    ) {
      return
    }

    if (IS_MAINNET && mainnetBlacklistedIDOs.includes(ido.contractAddress)) {
      return
    }

    if (
      ido.contractAddress !== 'inj1f9a2v82emja2z9phv3xrqs3v64a702ars37djl' &&
      !ido.isPermissionless &&
      IS_MAINNET &&
      !appStore.hasAdminPrivileges &&
      !mainnetWhitelistedIDOs.includes(ido.contractAddress) &&
      sharedWalletStore.injectiveAddress !== ido.owner
    ) {
      return
    }

    const market = spotStore.markets.find(
      (market: UiMarket) => market.marketId === ido.marketId
    )

    if (!market) {
      return
    }

    let status =
      Object.values(IDOStatus).find((status) => status === ido.status) ||
      (ido.status as IDOStatus)

    if (ido.contractAddress === 'inj1f9a2v82emja2z9phv3xrqs3v64a702ars37djl') {
      status = IDOStatus.Allocating
    }

    const description =
      launchpadDescriptionMapping[ido.marketId] || 'No description.'

    const projectTokenAmount = sharedToBalanceInToken({
      value: ido.projectTokenAmount || 0,
      decimalPlaces: market.baseToken.decimals
    })

    const targetAmountInQuoteDenomInToken = sharedToBalanceInTokenInBase({
      value: ido.targetAmountInQuoteDenom || 0,
      decimalPlaces: market.quoteToken.decimals
    })

    const tokenPriceInDust = sharedToBalanceInWei({
      value: targetAmountInQuoteDenomInToken
        .dividedBy(projectTokenAmount)
        .toNumber(),
      decimalPlaces: market.quoteToken.decimals
    })

    const tokenPriceInUsd = new BigNumberInBase(ido.targetAmountInUsd)
      .dividedBy(projectTokenAmount)
      .toFixed()

    const capPerAddress = sharedToBalanceInTokenInBase({
      value: ido.capPerAddress,
      decimalPlaces: market.quoteToken.decimals
    })

    const stakingRequirement = [...ido.stakeToSubscription].pop()
    const isRemoveStakingRequirement =
      !!stakingRequirement &&
      new BigNumberInBase(stakingRequirement.stakedAmount).isZero()

    return {
      ...ido,
      status,
      market,
      description,
      tokenPriceInUsd,
      isRemoveStakingRequirement,
      tokenPrice: tokenPriceInDust.toFixed(0),
      token: getDefaultTokenMeta({ market }),
      isHideMaxContribute:
        capPerAddress.eq(0) || capPerAddress.eq(LAUNCHPAD_DEFAULT_MAX_CAP)
    }
  }

  static subscribersResponseToUiSubscribers({
    marketId,
    subscribers
  }: {
    marketId: string
    subscribers: MitoIDOSubscriber[]
  }): UIIDOSubscriber[] {
    const spotStore = useSpotStore()

    const market = spotStore.markets.find(
      (market: UiMarket) => market.marketId === marketId
    )

    if (!market) {
      return []
    }

    return subscribers.map((subscriber) => ({
      ...subscriber,
      market,
      token: getDefaultTokenMeta({ market })
    }))
  }

  static subscriptionToUiSubscription({
    subscription,
    contractAddress
  }: {
    subscription?: MitoIDOSubscription
    contractAddress: string
  }): UIIDOSubscription | undefined {
    if (!subscription) {
      return
    }

    const spotStore = useSpotStore()
    const tokenStore = useTokenStore()

    const market = spotStore.markets.find(
      (market: UiMarket) => market.marketId === subscription.marketId
    )

    if (!market) {
      return
    }

    const subscribableAmount = new BigNumberInBase(
      subscription.maxSubscriptionCoin?.amount || 0
    ).minus(subscription.committedAmount || 0)

    const lpTokenDenom = subscription.claimableCoins.find(
      ({ denom }) =>
        denom.startsWith('factory') && denom !== subscription.tokenInfo?.denom
    )

    if (lpTokenDenom) {
      tokenStore.appendMitoLpToken(
        getDefaultTokenMeta({
          market,
          denom: lpTokenDenom.denom
        })
      )
    }

    const claimedCoins = hideClaimedTokenVaults.includes(contractAddress)
      ? undefined
      : subscription.claimedCoins

    return {
      ...subscription,
      market,
      claimedCoins,
      subscribableAmount: BigNumber.max(0, subscribableAmount).toFixed()
    }
  }

  static claimReferenceToClaimReference(
    claimReference: MitoClaimReference
  ): UIClaimReference {
    const endVestingTime =
      sharedConvertTimestampToMilliseconds(claimReference.startVestingTime) +
      claimReference.vestingDurationSeconds * 1000

    return { ...claimReference, endVestingTime }
  }
}
